import { useState } from 'react';
import {
  Button,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useDuplicateSubsidiaryGroupMutation, useGetSubsidiaryGroupsQuery } from '@api/subsidiary-groups';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { hasInvalidCharacters, isNameDuplicate, getInvalidNameMessage } from './utils';

interface Props {
  initiativeId: string;
  group: { groupId: string; groupName: string } | null;
  onClose: () => void;
}

export const DuplicateSubsidiaryGroupModal = ({ initiativeId, group, onClose }: Props) => {
  const [newName, setNewName] = useState(() => (group ? `${group.groupName} Copy` : ''));
  const [duplicateGroup, { isLoading: isDuplicating }] = useDuplicateSubsidiaryGroupMutation();
  const { data: existingGroups } = useGetSubsidiaryGroupsQuery(initiativeId);

  const handleConfirm = async () => {
    if (!group) return;

    try {
      await duplicateGroup({
        initiativeId,
        groupId: group.groupId,
        newName: newName.trim(),
      }).unwrap();
      onClose();
      generateToast({
        color: 'success',
        title: 'Success',
        message: 'Subsidiary group duplicated successfully',
      });
    } catch (err) {
      toastFromError({ error: err, title: 'Failed to duplicate group' });
      onClose();
    }
  };

  const isDuplicateNameTaken = isNameDuplicate(newName, existingGroups ?? []);
  const hasInvalidChars = hasInvalidCharacters(newName);
  const invalidNameMessage = getInvalidNameMessage(newName, isDuplicateNameTaken, hasInvalidChars);
  const isValid = newName.trim() && !invalidNameMessage;

  return (
    <Modal isOpen={!!group} toggle={onClose} backdrop='static'>
      <ModalHeader toggle={onClose}>Duplicate Subsidiary Group</ModalHeader>
      <ModalBody>
        <p>
          Create a copy of <strong>{group?.groupName}</strong> with a new name.
        </p>
        <FormGroup>
          <Label for='duplicateName' className='fw-bold'>
            New group name <span className='text-ThemeDangerMedium'>*</span>
          </Label>
          <Input
            id='duplicateName'
            type='text'
            placeholder='Enter new group name'
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            invalid={!!invalidNameMessage}
            disabled={isDuplicating}
          />
          {invalidNameMessage && <FormFeedback className='d-block'>{invalidNameMessage}</FormFeedback>}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={onClose} disabled={isDuplicating}>
          Cancel
        </Button>
        <Button color='primary' onClick={handleConfirm} disabled={!isValid || isDuplicating}>
          {isDuplicating ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
